
// Check if crypto.randomUUID is available to use, some unsecure context might not have this
// If missing we fallback to unsafeGenerateUUID which use Math.random, Date.now and performance.now
// which is not cryptographically secure, but for our use case it's fine.
let canUseCrypto = false;
try {
    crypto.randomUUID();
    canUseCrypto = true;
} catch (e) {
    console.warn("Linguise: crypto.randomUUID is not available, using unsafe UUID generator");
}

/**
 * Ignored element node
 * 
 * https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeName
 * 
 * Node name are uppercased.
 * 
 * @type {string[]}
 */
const IGNORED_NODE_NAME = [
    // Scripting/Head
    "SCRIPT",
    "NOSCRIPT",
    "HEAD",
    "META",
    "STYLE",
    "IFRAME",
    "OBJECT",
    "SELECT",
    // Multimedia
    "AREA",
    "AUDIO",
    "SVG",
    "MATH",
    "CANVAS",
    "MAP",
    "AUDIO",
    "TRACK",
    "VIDEO",
];

/**
 * An extended list of ignored node name
 *
 * Used to check if the parent element is ignored (if the element is #text)
 * 
 * @type {string[]}
 */
const EXTENDED_IGNORED_NODE_NAME = [
    ...IGNORED_NODE_NAME,
    "TEXTAREA",
    "INPUT",
];

/**
 * A list of node name that use custom tokenizer
 * 
 * See also {@link NODE_TOKENIZER_MAPPING}
 * 
 * The following maps from node name to a list of attribute names that should be tokenized.
 * 
 * @type {{[key: string]: string[]}}
 */
const NODE_TOKENIZER_CUSTOM_ATTRS = {
    "IMG": ["alt", "title", "src"],
    "A": ["href"],
    "INPUT": ["placeholder"],
    "TEXTAREA": ["placeholder"],
};

/**
 * Do an unsafe UUID generation, this is not cryptographically secure
 *
 * Reference: https://stackoverflow.com/a/8809472 (MIT/Public domain licensed)
 *
 * @returns {string} UUID
 */
function unsafeGenerateUUID() {
    // Timestamp
    let d = new Date().getTime();
    // Time in microseconds since page-load or 0 if unsupported
    let d2 = (typeof performance !== "undefined" && performance.now && performance.now() * 1000) || 0;
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        var r = Math.random() * 16; // random number between 0 and 16
        if (d > 0) {
            // Use timestamp until depleted
            r = (d + r) % 16 | 0;
            d = Math.floor(d / 16);
        } else {
            // Use microseconds since page-load if supported
            r = (d2 + r) % 16 | 0;
            d2 = Math.floor(d2 / 16);
        }
        return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
}

/**
 * Generate an UUID
 *
 * This will fallback to unsafeGenerateUUID if crypto is not available
 *
 * @returns {string} UUID
 */
function generateUUID() {
    if (canUseCrypto) {
        return crypto.randomUUID();
    }
    return unsafeGenerateUUID();
}

/**
 * Debounce a function
 * @param {(...args) => void} callback The callback to be called
 * @param {*} delay timeout delay
 * @returns {(...args) => void} The debounced function
 */
function debounce(callback, delay) {
    /** @type {number | null} */
    let timeoutId = null;

    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => {
            callback(...args);
        }, delay);
    }
}

export {
    debounce,
    generateUUID,
    IGNORED_NODE_NAME,
    EXTENDED_IGNORED_NODE_NAME,
    NODE_TOKENIZER_CUSTOM_ATTRS,
}
